// Reusable shell for module landing pages. Each landing supplies its own content.
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Phone, MapPin, ChevronRight } from 'lucide-react';
import ModuleNav from '@/components/layout/ModuleNav';
import AIChatWidget from '@/components/AIChatWidget';
import api from '@/lib/api';
import { formatApiError } from '@/lib/auth';

const HQS = [
  { city: 'Lagos', state: 'Lagos', country: 'Nigeria', flag: '🇳🇬' },
  { city: 'London', state: 'England', country: 'UK', flag: '🇬🇧' },
  { city: 'Houston', state: 'Texas', country: 'USA', flag: '🇺🇸' },
  { city: 'Toronto', state: 'Ontario', country: 'Canada', flag: '🇨🇦' },
];

export default function ModuleLandingShell({ module, theme, menuItems, children, formSourceTag }) {
  return (
    <div style={{ background: theme.bg, color: '#1A1A2E' }} data-testid={`landing-${module.slug}`}>
      <ModuleNav module={module} theme={theme} items={menuItems} />
      {children}
      <SubscribeSection module={module} theme={theme} sourceTag={formSourceTag} />
      <ModuleFooter module={module} theme={theme} />
      <AIChatWidget context={`Visitor on ATTP ${module.short} landing page.`} />
    </div>
  );
}

export function Hero({ theme, badge, headline, sub, ctas = [], imageUrl, accentText }) {
  return (
    <section id="home" className="relative overflow-hidden" style={{ background: theme.primary, color: theme.textOnPrimary }}>
      <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full blur-[120px]" style={{ background: `${theme.accent}25` }} />
      <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] rounded-full blur-[120px]" style={{ background: `${theme.secondary}25` }} />
      <div className="container-x relative pt-16 pb-20 lg:pt-24 lg:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          {badge && (
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.18em] mb-6"
              style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}>
              <span className="h-1.5 w-1.5 rounded-full animate-pulse" style={{ background: theme.accent }} />
              {badge}
            </div>
          )}
          <h1 className="font-display font-bold tracking-tight text-4xl sm:text-5xl lg:text-[58px] leading-[1.05] text-balance">
            {accentText ? (
              <>
                {headline} <span style={{ color: theme.accent }}>{accentText}</span>
              </>
            ) : headline}
          </h1>
          {sub && <p className="mt-6 text-lg leading-relaxed max-w-2xl" style={{ color: 'rgba(255,255,255,0.78)' }}>{sub}</p>}
          <div className="mt-8 flex flex-wrap gap-3">
            {ctas.map((c, i) => (
              <a key={c.label} href={c.href}
                className={`px-6 py-3 rounded-md font-semibold text-sm transition-all hover:scale-105 inline-flex items-center gap-2 ${i === 0 ? '' : 'border'}`}
                style={i === 0
                  ? { background: theme.accent, color: theme.primary }
                  : { border: '1px solid rgba(255,255,255,0.25)', color: theme.textOnPrimary }
                }>
                {c.label} {i === 0 && <ChevronRight className="w-4 h-4" />}
              </a>
            ))}
          </div>
        </div>
        {imageUrl && (
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] rounded-md overflow-hidden border shadow-2xl" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
              <img src={imageUrl} alt={module.short} className="w-full h-full object-cover" />
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

export function Section({ id, theme, label, title, sub, children, alt = false }) {
  return (
    <section id={id} className={`py-16 lg:py-20 ${alt ? '' : 'bg-white'}`} style={alt ? { background: theme.bg } : {}}>
      <div className="container-x">
        {(label || title) && (
          <div className="max-w-3xl mb-10">
            {label && (
              <div className="text-xs uppercase tracking-[0.22em] font-semibold mb-3" style={{ color: theme.accent }}>
                {label}
              </div>
            )}
            {title && <h2 className="font-display font-bold text-3xl lg:text-4xl tracking-tight leading-tight text-balance">{title}</h2>}
            {sub && <p className="mt-4 text-base text-neutral-600 leading-relaxed">{sub}</p>}
          </div>
        )}
        {children}
      </div>
    </section>
  );
}

export function FeatureGrid({ theme, items }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {items.map((f) => (
        <div key={f.title} className="p-5 bg-white border rounded-md hover:shadow-md transition-all hover:-translate-y-0.5"
          style={{ borderColor: '#E5E7EB' }}>
          <div className="h-10 w-10 rounded-md flex items-center justify-center mb-3" style={{ background: `${theme.accent}1A`, color: theme.accent }}>
            {f.icon && <f.icon className="w-5 h-5" strokeWidth={1.6} />}
          </div>
          <div className="font-display font-semibold text-base mb-1.5" style={{ color: theme.primary }}>{f.title}</div>
          <p className="text-sm text-neutral-600 leading-relaxed">{f.desc}</p>
        </div>
      ))}
    </div>
  );
}

export function Steps({ theme, steps }) {
  return (
    <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {steps.map((s, i) => (
        <li key={i} className="p-5 bg-white border rounded-md" style={{ borderColor: '#E5E7EB' }}>
          <div className="text-xs font-mono mb-2" style={{ color: theme.accent }}>STEP {String(i + 1).padStart(2, '0')}</div>
          <div className="font-display font-semibold text-base mb-1" style={{ color: theme.primary }}>{s.title}</div>
          {s.desc && <p className="text-sm text-neutral-600 leading-relaxed">{s.desc}</p>}
        </li>
      ))}
    </ol>
  );
}

function SubscribeSection({ module, theme, sourceTag }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', organization: '', org_size: '', country: '', message: '' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setBusy(true);
    try {
      await api.post('/contact/submit', { ...form, modules: [module.slug], tools_interest: sourceTag || module.short });
      setDone(true);
    } catch (err) { setError(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <section id="subscribe" className="py-16 lg:py-20 bg-white">
      <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="text-xs uppercase tracking-[0.22em] font-semibold mb-3" style={{ color: theme.accent }}>Get started</div>
          <h2 className="font-display font-bold text-3xl lg:text-4xl tracking-tight leading-tight" style={{ color: theme.primary }}>
            Ready to subscribe to {module.short}?
          </h2>
          <p className="mt-4 text-neutral-600 leading-relaxed">
            Fill the form. Our team will review and set up your account within 24 hours.
          </p>
          <div className="mt-6 space-y-2">
            <div className="flex items-center gap-2 text-sm"><Mail className="w-4 h-4" style={{ color: theme.accent }} /> info@attpnexus.com.ng</div>
            <div className="flex items-center gap-2 text-sm"><Phone className="w-4 h-4" style={{ color: theme.accent }} /> +234 (703) 313-7679</div>
            <div className="flex items-start gap-2 text-sm"><MapPin className="w-4 h-4 mt-0.5" style={{ color: theme.accent }} />
              <span>Lagos · London · Houston · Toronto</span>
            </div>
          </div>
        </div>
        <div className="lg:col-span-7 rounded-md border p-6" style={{ background: theme.bg, borderColor: '#E5E7EB' }}>
          {done ? (
            <div className="p-6 text-center">
              <div className="font-display font-bold text-xl mb-2" style={{ color: theme.accent }}>Thank you.</div>
              <p className="text-sm text-neutral-700">Your profile will be set up and login details will be sent within 24 hours via email.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <FormField id="full_name" name="full_name" label="Full name *" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="module-subscribe-full_name" />
                <FormField id="email" name="email" label="Email *" type="email" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="module-subscribe-email" />
                <FormField id="phone" name="phone" label="Phone" type="tel" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="module-subscribe-phone" />
                <FormField id="country" name="country" label="Country" value={form.country} onChange={(v) => setForm({ ...form, country: v })} testId="module-subscribe-country" />
                <FormField id="organisation" name="organisation" label="Organisation" value={form.organization} onChange={(v) => setForm({ ...form, organization: v })} testId="module-subscribe-organisation" />
                <FormField id="org_size" name="org_size" label="Organisation size" value={form.org_size} onChange={(v) => setForm({ ...form, org_size: v })} testId="module-subscribe-org_size" />
              </div>
              <FormField id="message" name="message" label="Message *" required textarea value={form.message} onChange={(v) => setForm({ ...form, message: v })} testId="module-subscribe-message" />
              {error && <div className="p-3 bg-rose-50 border border-rose-200 rounded text-sm text-rose-700">{error}</div>}
              <button type="submit" disabled={busy}
                className="w-full py-2.5 rounded-md font-semibold text-sm transition-all disabled:opacity-60"
                style={{ background: theme.accent, color: theme.primary }}
                data-testid="module-subscribe-submit">
                {busy ? 'Sending…' : 'Submit Request'}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function FormField({ id, name, label, value, onChange, type = 'text', required, textarea, testId }) {
  return (
    <label className="block" htmlFor={id}>
      <span className="block text-[11px] uppercase tracking-wider text-neutral-600 font-semibold mb-1.5">{label}</span>
      {textarea ? (
        <textarea id={id} name={name} rows={3} value={value} required={required} data-testid={testId} onChange={(e) => onChange(e.target.value)}
          className="w-full px-3 py-2 border border-neutral-200 rounded-md outline-none focus:border-neutral-500 bg-white" />
      ) : (
        <input id={id} name={name} type={type} value={value} required={required} data-testid={testId} onChange={(e) => onChange(e.target.value)}
          className="w-full px-3 py-2 border border-neutral-200 rounded-md outline-none focus:border-neutral-500 bg-white" />
      )}
    </label>
  );
}

function ModuleFooter({ module, theme }) {
  return (
    <footer style={{ background: theme.primary, color: theme.textOnPrimary }}>
      <div className="container-x py-12 grid grid-cols-1 md:grid-cols-3 gap-8">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <img src={module.logo} alt="" className="h-9 w-9 object-contain" />
            <div className="font-display font-bold text-lg">{module.short}</div>
          </div>
          <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.7)' }}>{module.tagline}</p>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.22em] font-semibold mb-3" style={{ color: theme.accent }}>Contact</div>
          <div className="space-y-1.5 text-sm" style={{ color: 'rgba(255,255,255,0.75)' }}>
            <div>info@attpnexus.com.ng</div>
            <div>+234 (703) 313-7679</div>
            <div>attpnexus.com.ng</div>
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.22em] font-semibold mb-3" style={{ color: theme.accent }}>Headquarters</div>
          <div className="space-y-1.5 text-sm" style={{ color: 'rgba(255,255,255,0.75)' }}>
            {HQS.map((h) => <div key={h.country}>{h.flag} {h.city}, {h.state}, {h.country}</div>)}
          </div>
        </div>
      </div>
      <div className="border-t py-4 text-center text-xs" style={{ borderColor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)' }}>
        © {new Date().getFullYear()} ATTP NEXUS. All rights reserved.
      </div>
    </footer>
  );
}
